<template>
	<div class="mega-menu">
		<v-menu
			v-if="isDesktop"
			v-model="isMenuOpen"
			:open-delay="0"
			:close-delay="20"
			:content-class="{ 'mega-menu-overlay': true, 'mega-menu-overlay--topbar-visible': isTopBarVisible }"
			location="bottom"
			:disabled="menuDisabled"
			:close-on-content-click="false"
			scroll-strategy="close"
			transition="scroll-y-transition"
			:offset="72"
			open-on-hover
			eager
			:z-index="2051">
			<template #activator="{ props }">
				<button v-bind="props" class="nav__item nav__item--dropdown">
					<span>{{ $t(`components.nav.${item.translation_key_label}`) }}</span>
					<GlobalIcon
						name="arrow-down"
						:height="18"
						class="mega-menu__icon"
						:class="{ 'mega-menu__icon--expanded': props['aria-expanded'] === 'true' }" />
				</button>
			</template>
			<Transition name="menu-fade">
				<div v-show="isMenuOpen" class="mega-menu-overlay__dropdown" @mouseleave="isMenuOpen = false">
					<template v-if="hasBeenOpened">
						<div class="mega-menu-overlay__menus">
							<div class="mega-menu-overlay__menu mega-menu-overlay__menu--first">
								<template v-for="(section, l) in computedMenu" :key="l">
									<div v-if="l !== 0" class="mega-menu-overlay__divider"></div>
									<div class="mega-menu-overlay__menu-title">
										{{ section.title }}
									</div>
									<div class="mega-menu-overlay__menu-links mega-menu-overlay__menu-links--main">
										<div v-for="(menuItem, j) in computedMenu[l].items" :key="j" class="mega-menu-overlay__menu-link-container">
											<ButtonCustom
												:to="localePath(menuItem?.link) || undefined"
												class="mega-menu-overlay__menu-link"
												@mouseover="updateLastHoveredCategory(menuItem)"
												@click="onMenuItemClick(menuItem)">
												<GlobalIcon
													v-if="menuItem.icon"
													:name="menuItem.icon"
													:height="18"
													:width="18"
													color="var(--primary-color)"
													class="mega-menu-overlay__menu-link-main-icon" />
												<div class="mega-menu-overlay__menu-link-info">
													<div class="mega-menu-overlay__menu-link-text">
														{{ menuItem.title }}
													</div>
													<div v-if="menuItem.subText" class="mega-menu-overlay__menu-link-subtext">
														{{ menuItem.subText }}
													</div>
												</div>
												<GlobalIcon
													v-if="menuItem.menu || (menuItem.items && menuItem.items.length > 0)"
													name="arrow-down"
													:height="14"
													:width="12"
													class="mega-menu-overlay__menu-link-icon" />
											</ButtonCustom>
										</div>
									</div>
								</template>
							</div>
							<Transition name="menu-fade-x" mode="out-in">
								<div v-if="lastHoveredCategory" class="mega-menu-overlay__submenu">
									<div
										v-if="lastHoveredCategory?.items || lastHoveredCategory?.menu"
										class="mega-menu-overlay__submenu-links mega-menu-overlay__submenu-links--main">
										<div
											v-for="(subItem, j) in lastHoveredCategory?.items"
											:key="j"
											class="mega-menu-overlay__submenu-link-container">
											<ButtonCustom
												:to="localePath(subItem?.link) || undefined"
												class="mega-menu-overlay__submenu-link"
												@click="onSubItemClick(lastHoveredCategory, subItem)">
												<span>{{ subItem.title }}</span>
											</ButtonCustom>
										</div>
										<template v-if="areCustomItems(lastHoveredCategory.translation_key_title)">
											<div v-for="subItem in customItems" :key="subItem.to" class="mega-menu-overlay__submenu-link-container">
												<ButtonCustom :to="subItem?.to || undefined" class="mega-menu-overlay__submenu-link">
													<span>
														{{
															i18nService.$tDynamic(
																'components.nav.mega_menu',
																subItem.translation_key_title || subItem.title || '',
															)
														}}
													</span>
												</ButtonCustom>
											</div>
										</template>
									</div>
									<!-- <div
									v-else-if="lastHoveredCategory?.menu"
									class="mega-menu-overlay__submenu-links mega-menu-overlay__submenu-links--center">
									<GlobalMegaMenuCheckOffer />
								</div> -->
								</div>
							</Transition>
						</div>
						<div ref="carsListingRef" class="mega-menu-overlay__swiper">
							<Swiper
								:modules="[Scrollbar, Navigation]"
								:navigation="true"
								:scrollbar="{
									hide: false,
								}"
								class="mega-menu__swiper"
								:class="{ 'mega-menu__swiper--loading': !cars || cars.length < 1 || megaMenuStore.getAreCarsLoading }"
								slidesPerView="auto"
								:space-between="16"
								@swiper="setSwiper">
								<template v-if="cars && cars.length && !megaMenuStore.getAreCarsLoading">
									<SwiperSlide v-for="(car, j) in cars" :key="j" class="mega-menu__swiper-slide">
										<CarsSingleCar :car="car" :index="j" home @click="isMenuOpen = false" @closeMegaMenu="isMenuOpen = false" />
									</SwiperSlide>
									<SwiperSlide class="mega-menu__swiper-slide">
										<NuxtLink :to="listingLink" class="mega-menu__placeholder" @click="isMenuOpen = false">
											<span class="mega-menu__placeholder-icon">
												<GlobalIcon name="arrow-right" :height="24" />
											</span>
											<span class="mega-menu__placeholder-text">
												{{ $t('components.news.section.see_all') }}
											</span>
										</NuxtLink>
									</SwiperSlide>
								</template>
								<template v-else>
									<SwiperSlide v-for="k in 10" :key="k" :slidesPerView="4" :space-between="16" class="mega-menu__swiper-slide">
										<Skeletor class="mega-menu__skeletor" round :height="497" />
									</SwiperSlide>
								</template>
							</Swiper>
						</div>
					</template>
				</div>
			</Transition>
		</v-menu>
	</div>
</template>

<script setup lang="ts">
import { Scrollbar, Navigation } from 'swiper/modules'
import { Skeletor } from 'vue-skeletor'
import debounce from 'lodash/debounce'
import { useMegaMenu } from './useMegaMenu'
import 'swiper/css/navigation'
import 'swiper/css/scrollbar'

const localePath = useLocalePath()
const { isDesktop } = useWindowWidth()
const isTopBarVisible = inject<Ref<boolean>>('isTopBarVisible', ref(false))
const lastHoveredCategory = ref<any>(null)
const swiperRef = ref<any>(null)
const groups = ref<IProductGroupModel[]>([])
const cars = ref<IProductModel[]>([])
const carsStore = useCarsStore()
const megaMenuStore = useMegaMenuStore()
const hoveredGroup = ref<string | undefined>()
const menuDisabled = ref(false)

const listingLink = computed(() => {
	const query = hoveredGroup.value ? { group: String(hoveredGroup.value) } : undefined
	return localePath({ name: 'cars', query })
})
const i18nService = usei18nService()

const { computedMenu, areCustomItems, getCustomItemsFor, handleRentalLengthFromLink, ensureLocationsLoaded } = useMegaMenu()

const customItems = computed(() => getCustomItemsFor(lastHoveredCategory.value))

const isMenuOpen = ref(false)
const hasBeenOpened = ref(false)
provide('isMenuOpen', isMenuOpen)

// Analytics
const carsListingRef = ref<HTMLElement | null>(null)
useSearchListingViewed(carsListingRef, cars, 'Mega menu')

const sendAnalyticsData = (item: any, subItem?: any) => {
	const gtmService = useGTMService()
	if (!gtmService) return

	const mainItem = { ...item, ...(subItem || {}) }

	const GTMParameters = {
		section: 'navigation',
		element: 'navigation-link',
		element_value: mainItem?.link,
		// by city
		location: mainItem.location,
		// by length of rental
		rental_length: mainItem.rental_length,
		// by car category
		category: mainItem.category,
	}
	gtmService.offerInterest(GTMParameters)
}

const onSubItemClick = (item: any, subItem: any) => {
	handleRentalLengthFromLink(subItem.link)
	isMenuOpen.value = false
	sendAnalyticsData(item, subItem)
}

const onMenuItemClick = (item: any) => {
	handleRentalLengthFromLink()
	isMenuOpen.value = false
	sendAnalyticsData(item)
}

const fetchGroups = async () => {
	try {
		const response = await carsStore.fetchAllGroups()
		groups.value = response
		return response
	} catch (error) {
		logger.error(error)
	}
}

// onMounted(() => {
// 	fetchGroups()
// })

let currentAbortController: AbortController | null = null

const fetchCars = async (group?: string) => {
	cars.value = []
	if (!groups.value.length) await fetchGroups()

	if (currentAbortController) {
		currentAbortController.abort()
	}
	currentAbortController = new AbortController()
	const signal = currentAbortController.signal

	try {
		const fetchedCars = await megaMenuStore.fetchCarsWithFilters(groups.value, group ? [group] : undefined, { signal })
		if (signal.aborted) return
		if (fetchedCars.response) cars.value = fetchedCars.response
		else cars.value = fetchedCars
	} catch (err: any) {
		if (err?.name !== 'AbortError' && !signal.aborted) {
			logger.error(err)
		}
	}
}

const debouncedFetchCars = debounce((group?: string) => fetchCars(group), 300)

defineProps<{
	item: {
		translation_key_label: string
		to: string
		type: string
	}
}>()

const updateLastHoveredCategory = async (category: any) => {
	if (category?.translation_key_title === lastHoveredCategory.value?.translation_key_title) return

	if (category?.filters && hoveredGroup.value !== category.filters[0]) {
		hoveredGroup.value = category.filters[0]
	} else if (!category?.filters && hoveredGroup.value) {
		hoveredGroup.value = undefined
	}

	if ((!category.items || category.items.length === 0) && !category.menu) {
		clearLastHoveredCategory()
		return
	}

	await nextTick()
	if (lastHoveredCategory.value) {
		lastHoveredCategory.value = null
	}
	setTimeout(() => {
		lastHoveredCategory.value = category
	}, 100)
}

const clearLastHoveredCategory = async () => {
	if (!lastHoveredCategory.value) return
	await nextTick()
	setTimeout(() => {
		lastHoveredCategory.value = null
	}, 100)
}

watch(
	() => isMenuOpen.value,
	(newValue) => {
		if (!newValue) {
			menuDisabled.value = true
			setTimeout(() => {
				menuDisabled.value = false
			}, 300)
		}
	},
)

watch(
	() => hoveredGroup.value,
	() => {
		debouncedFetchCars(hoveredGroup.value)
		if (swiperRef.value) swiperRef.value.activeIndex = 0
	},
)

watch(
	() => isMenuOpen.value,
	async (isOpen) => {
		if (!isOpen) {
			clearLastHoveredCategory()
			hoveredGroup.value = undefined
			debouncedFetchCars.cancel()
			currentAbortController?.abort()
			currentAbortController = null
		} else {
			if (!hasBeenOpened.value) hasBeenOpened.value = true
			fetchCars(hoveredGroup.value)
			await ensureLocationsLoaded()
		}
	},
)

const setSwiper = (swiper: any) => {
	swiperRef.value = swiper
}
</script>

<style lang="scss">
@import 'vue-skeletor/dist/vue-skeletor.css';
</style>
<style lang="scss" src="./MegaMenu.scss"></style>
<style lang="scss">
.menu-fade-enter-active {
	transition:
		transform 0.1s ease-out,
		opacity 0.1s ease-out;
}

.menu-fade-leave-active {
	transition:
		transform 0.1s ease-in,
		opacity 0.1s ease-in;
}

.menu-fade-enter-from,
.menu-fade-leave-to {
	transform: translateY(-20px);
	opacity: 0;
}

.menu-fade-x-enter-active {
	transition:
		transform 0.1s ease-out,
		opacity 0.1s ease-out;
}

.menu-fade-x-leave-active {
	transition:
		transform 0.1s ease-in,
		opacity 0.1s ease-in;
}

.menu-fade-x-enter-from,
.menu-fade-x-leave-to {
	transform: translateX(-20px);
	opacity: 0;
}
</style>
